import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useOutletContext } from 'react-router'
import { httpAddImagesFlag } from '@/features/photoset/http/photoset.http'
import {
	type Flag,
	type Image,
	type Photoset,
	type PhotosetSpace,
	type PhotosetURLParams,
	StepKey
} from '@/features/photoset/types'
import {
	updateCachedEditingQaFlags,
	updateCachedImagesFlag,
	updateCachedSpaceImagesFlag
} from '@/features/photoset/utils'
import { errorHandler } from '@/helpers/utils'
import { MUTATION_KEYS, QUERY_KEYS } from '@/query-client'
import type { ErrorResponseData } from '@/types/commons'

interface AddImagesFlagData {
	flag: Flag
	imageIds: Image['id'][]
}

interface UseAddImagesFlagOptions {
	onSuccess?: () => void
	onError?: (error: AxiosError<ErrorResponseData>) => void
}

export const useAddImagesFlag = ({
	stepId,
	photosetSpaceIds,
	options
}: {
	stepId: Photoset['step_id']
	photosetSpaceIds?: PhotosetSpace['id'][]
	options?: UseAddImagesFlagOptions
}) => {
	const queryClient = useQueryClient()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()

	return useMutation({
		...options,
		mutationKey: [MUTATION_KEYS.ASSIGN_IMAGES_FLAG],
		mutationFn: (data: AddImagesFlagData) =>
			httpAddImagesFlag({
				urlParams: { photosetId: urlParams.id, flagId: data.flag.id },
				payload: { imageIds: data.imageIds }
			}),
		onMutate: async (vars: AddImagesFlagData) => {
			// Update get-images cache is the base behaviour
			const previousImages = await updateCachedImagesFlag({
				operation: 'add',
				updatedFlag: vars.flag,
				imageIdsToUpdate: vars.imageIds
			})

			let previousSpaceImages: PhotosetSpace[] = []

			// Optimistic update (curation step and retouching qa step)
			if (
				(stepId === StepKey.curation ||
					stepId === StepKey['retouching-qa']) &&
				photosetSpaceIds
			) {
				previousSpaceImages = await updateCachedSpaceImagesFlag({
					operation: 'add',
					photosetSpaceIds,
					updatedFlag: vars.flag,
					imageIdsToUpdate: vars.imageIds
				})
			}

			// Optimistic update (editing qa step)
			if (stepId === StepKey['editing-qa-flags'] && photosetSpaceIds) {
				previousSpaceImages = await updateCachedEditingQaFlags({
					operation: 'add',
					photosetSpaceIds,
					updatedFlag: vars.flag,
					imageIdsToUpdate: vars.imageIds
				})
			}

			// Return a context object with the snapshotted value
			return { previousImages, previousSpaceImages }
		},
		onError: (error: AxiosError<ErrorResponseData>, _, context) => {
			queryClient.setQueriesData(
				{ queryKey: [QUERY_KEYS.IMAGES] },
				context?.previousImages
			)

			if (
				stepId === StepKey.curation ||
				stepId === StepKey['retouching-qa']
			) {
				queryClient.setQueriesData(
					{ queryKey: [QUERY_KEYS.PHOTOSET_SPACES] },
					context?.previousSpaceImages
				)
			}

			options?.onError?.(error)
			errorHandler(error)
		},
		onSuccess: () => options?.onSuccess?.()
	})
}
